import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import * as LuIcons from 'react-icons/lu';
import SafeIcon from '../common/SafeIcon';
import { motion, AnimatePresence } from 'framer-motion';
import Lightbox from './Lightbox';
import ShareMenu from './ShareMenu';
import PricingBreakdown from './PricingBreakdown';
import DiscountTimer from './DiscountTimer';
import { supabase } from '../lib/supabase';

const { LuCalendar, LuGauge, LuChevronLeft, LuChevronRight, LuImages, LuZoomIn, LuEye, LuCheckCircle, LuSettings2, LuPalette, LuTag, LuPlayCircle } = LuIcons;

// دالة ذكية لسحب الصورة المصغرة للفيديو (خاصة يوتيوب)
const getVideoThumbnail = (url) => {
  if (!url) return null;
  try {
    if (url.includes('youtube.com/shorts/')) {
      const id = url.split('shorts/')[1].split('?')[0];
      return `https://i.ytimg.com/vi/${id}/hqdefault.jpg`;
    } else if (url.includes('watch?v=')) {
      const id = new URL(url).searchParams.get('v');
      return `https://i.ytimg.com/vi/${id}/hqdefault.jpg`;
    } else if (url.includes('youtu.be/')) {
      const id = url.split('youtu.be/')[1].split('?')[0];
      return `https://i.ytimg.com/vi/${id}/hqdefault.jpg`;
    }
  } catch (e) {}
  // صورة افتراضية للفيديوهات الأخرى (تيك توك وغيرها)
  return 'https://images.unsplash.com/photo-1611162616475-46b635cb6868?auto=format&fit=crop&q=80&w=600';
};

const ImageSlider = ({ media, alt, onImageClick, isSold, carId }) => {
  const [current, setCurrent] = useState(0);
  const navigate = useNavigate();

  if (!media || media.length === 0) return (
    <div className="relative w-full h-full cursor-zoom-in" onClick={() => onImageClick(0)}>
      <img src="https://images.unsplash.com/photo-1494905998459-36430ba12391?auto=format&fit=crop&q=80&w=600" alt={alt} className={`w-full h-full object-cover transition-all ${isSold ? 'grayscale-[40%]' : ''}`} />
    </div>
  );

  const currentItem = media[current];

  return (
    <div className="relative w-full h-full group">
      <AnimatePresence mode="wait">
        <motion.div 
          key={current} 
          initial={{ opacity: 0 }} 
          animate={{ opacity: 1 }} 
          exit={{ opacity: 0 }} 
          transition={{ duration: 0.2 }}
          className="w-full h-full relative"
          onClick={() => {
            if (currentItem.isVideo) {
              navigate(`/car/${carId}`); // إذا كان فيديو، نفتح صفحة التفاصيل لتشغيله
            } else {
              // حساب الاندكس الصحيح للصور فقط في اللايتبوكس
              const imageOnlyIndex = media.slice(0, current).filter(m => !m.isVideo).length;
              onImageClick(imageOnlyIndex);
            }
          }}
        >
          <img 
            src={currentItem.url} 
            alt={`${alt} - ${current + 1}`} 
            className={`w-full h-full object-cover ${currentItem.isVideo ? 'cursor-pointer' : 'cursor-zoom-in'} transition-all ${isSold ? 'grayscale-[40%]' : ''}`}
            onError={e => { e.target.src = 'https://images.unsplash.com/photo-1494905998459-36430ba12391?auto=format&fit=crop&q=80&w=600'; }}
          />
          {currentItem.isVideo && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/20 pointer-events-none">
              <SafeIcon icon={LuPlayCircle} className="w-12 h-12 text-white drop-shadow-xl" />
            </div>
          )}
        </motion.div>
      </AnimatePresence>
      
      {!currentItem.isVideo && (
        <div className="absolute inset-0 pointer-events-none flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
          <div className="bg-black/40 text-white rounded-full p-2.5 backdrop-blur-sm">
            <SafeIcon icon={LuZoomIn} className="w-5 h-5" />
          </div>
        </div>
      )}

      {media.length > 1 && (
        <>
          <button onClick={e => { e.stopPropagation(); setCurrent(c => c === 0 ? media.length - 1 : c - 1); }} className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full p-1.5 opacity-0 group-hover:opacity-100 transition-all z-10 backdrop-blur-sm">
            <SafeIcon icon={LuChevronRight} className="w-4 h-4" />
          </button>
          <button onClick={e => { e.stopPropagation(); setCurrent(c => c === media.length - 1 ? 0 : c + 1); }} className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full p-1.5 opacity-0 group-hover:opacity-100 transition-all z-10 backdrop-blur-sm">
            <SafeIcon icon={LuChevronLeft} className="w-4 h-4" />
          </button>
          <div className="absolute bottom-10 left-1/2 -translate-x-1/2 flex gap-1 z-10">
            {media.map((_, i) => (
              <button key={i} onClick={e => { e.stopPropagation(); setCurrent(i); }} className={`h-1 rounded-full transition-all ${i === current ? 'bg-white w-5' : 'bg-white/50 w-2'}`} />
            ))}
          </div>
          <div className="absolute top-2 left-2 bg-black/50 text-white text-xs px-2 py-1 rounded-full flex items-center gap-1 z-10 backdrop-blur-sm">
            <SafeIcon icon={LuImages} className="w-3 h-3" /> {media.length}
          </div>
        </>
      )}
    </div>
  );
};

const buildWhatsAppMessage = (car) => {
  const hasDiscount = car.discount_enabled && Number(car.discount_price) > 0;
  const displayPrice = hasDiscount ? Number(car.discount_price) : (Number(car.total_price) > 0 ? Number(car.total_price) : (Number(car.price) || 0) + (Number(car.customs_price) || 0) + (Number(car.shipping_price) || 0));
  
  // تم إزالة الهاشتاج من هنا
  const carUrl = `${window.location.origin}/car/${car.id}`;
  
  let message = `🚗 *السيارة:* ${car.brand} ${car.model}\n`;
  message += `📅 *الموديل:* ${car.year}\n`;
  message += `🛣️ *الممشى:* ${Number(car.mileage).toLocaleString()} كم\n`;
  if (car.color) message += `🎨 *اللون:* ${car.color}\n`;
  
  if (hasDiscount) message += `🏷️ *سعر مخفض:* ${displayPrice.toLocaleString()} دج\n`;
  else if (displayPrice > 0) message += `💰 *الإجمالي: ${displayPrice.toLocaleString()} دج*\n`;
  
  message += `\n🔗 ${carUrl}`;
  return message;
};

const CarCard = ({ car }) => {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [localViews, setLocalViews] = useState(car.views_count || 0);
  const navigate = useNavigate();

  const isSold = car.status === 'sold';
  const hasDiscount = car.discount_enabled && Number(car.discount_price) > 0;

  // بناء مصفوفة الوسائط (الفيديو أولاً كمصغّر إذا وجد، ثم الصور)
  const mediaContent = [];
  if (car.video_url) {
    mediaContent.push({ url: getVideoThumbnail(car.video_url), isVideo: true });
  }
  if (car.image_url) {
    mediaContent.push({ url: car.image_url, isVideo: false });
  }
  if (Array.isArray(car.gallery)) {
    car.gallery.filter(g => g && g !== car.image_url).forEach(g => {
      mediaContent.push({ url: g, isVideo: false });
    });
  }

  // استخراج الصور فقط من أجل اللايتبوكس
  const pureImages = mediaContent.filter(m => !m.isVideo).map(m => m.url);

  useEffect(() => {
    const newCount = (car.views_count || 0) + 1;
    setLocalViews(newCount);
    supabase.from('cars_for_sale').update({ views_count: newCount }).eq('id', car.id).then(() => {});
  }, []);

  const handleWhatsApp = () => window.open(`https://wa.me/${car.whatsapp}?text=${encodeURIComponent(buildWhatsAppMessage(car))}`, '_blank');

  const handleCardClick = (e) => {
    if (e.target.closest('button') || e.target.closest('a')) return;
    navigate(`/car/${car.id}`);
  };

  return (
    <>
      <motion.div 
        initial={{ opacity: 0, y: 20 }} 
        animate={{ opacity: 1, y: 0 }} 
        transition={{ duration: 0.35 }}
        whileHover={{ y: -3, transition: { duration: 0.2 } }}
        onClick={handleCardClick}
        className={`bg-white rounded-2xl shadow-sm border overflow-hidden transition-shadow duration-300 hover:shadow-lg flex flex-col cursor-pointer ${isSold ? 'border-red-200' : hasDiscount ? 'border-red-200' : 'border-slate-100'}`}
      >
        {/* Image / Video Thumbnail */}
        <div className="relative h-44 sm:h-52 w-full bg-slate-100 flex-shrink-0">
          <ImageSlider 
            media={mediaContent.length > 0 ? mediaContent : null} 
            alt={`${car.brand} ${car.model}`} 
            onImageClick={i => { setLightboxIndex(i); setLightboxOpen(true); }}
            isSold={isSold}
            carId={car.id}
          />
          
          {isSold && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-10">
              <div className="bg-red-600/90 text-white px-6 py-2 rounded-full shadow-lg backdrop-blur-sm flex items-center gap-2 rotate-[-5deg]">
                <SafeIcon icon={LuCheckCircle} className="w-4 h-4" />
                <span className="font-black text-base">تم البيع</span>
              </div>
            </div>
          )}

          {!isSold && !hasDiscount && (
            <div className="absolute top-2 right-2 bg-emerald-500/90 text-white text-xs font-bold px-2 py-1 rounded-full shadow-sm z-10 flex items-center gap-1 backdrop-blur-sm">
              <span className="w-1.5 h-1.5 bg-white rounded-full animate-pulse inline-block"></span>
              متوفرة
            </div>
          )}

          {!isSold && hasDiscount && (
            <div className="absolute top-2 right-2 bg-red-500/95 text-white text-xs font-black px-2 py-1 rounded-full shadow-sm z-10 flex items-center gap-1 backdrop-blur-sm animate-pulse">
              <SafeIcon icon={LuTag} className="w-3 h-3" />
              تخفيض!
            </div>
          )}

          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent px-3 py-2 z-10">
            <div className="flex items-center gap-1 text-white text-xs">
              <SafeIcon icon={LuEye} className="w-3 h-3 text-white/80" />
              <span className="font-semibold">{localViews.toLocaleString()} مشاهدة</span>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-3 sm:p-4 flex flex-col flex-1">
          <div className="mb-2">
            <h3 className={`text-sm sm:text-base font-black line-clamp-1 ${isSold ? 'text-slate-500' : 'text-slate-900'}`}>
              {car.brand} {car.model}
            </h3>
            <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
              <p className="text-xs text-slate-400">{car.year}</p>
              {car.condition === 'new' ? <span className="text-xs font-bold text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded-md">جديدة</span> : <span className="text-xs font-bold text-orange-500 bg-orange-50 px-1.5 py-0.5 rounded-md">مستعملة</span>}
              {car.transmission && (
                <span className={`text-xs font-bold px-1.5 py-0.5 rounded-md ${car.transmission === 'automatic' ? 'text-violet-600 bg-violet-50' : 'text-amber-600 bg-amber-50'}`}>
                  {car.transmission === 'automatic' ? 'أوتو' : 'يدوي'}
                </span>
              )}
              {isSold && <span className="text-xs font-bold text-red-500 bg-red-50 px-1.5 py-0.5 rounded-md">مباع</span>}
            </div>
          </div>

          <p className="text-slate-500 text-xs mb-2 line-clamp-2 leading-relaxed min-h-[32px]">
            {car.description || 'لا يوجد وصف متاح لهذه السيارة.'}
          </p>

          {/* Stats grid */}
          <div className="grid grid-cols-2 gap-1.5 mb-2">
            <div className="flex items-center bg-slate-50 px-2 py-1.5 rounded-xl gap-1.5">
              <div className="w-5 h-5 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <SafeIcon icon={LuCalendar} className="h-3 w-3 text-blue-600" />
              </div>
              <span className="text-xs font-bold text-slate-700">{car.year}</span>
            </div>
            <div className="flex items-center bg-slate-50 px-2 py-1.5 rounded-xl gap-1.5">
              <div className="w-5 h-5 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <SafeIcon icon={LuGauge} className="h-3 w-3 text-purple-600" />
              </div>
              <span className="text-xs font-bold text-slate-700 truncate">{Number(car.mileage).toLocaleString()} كم</span>
            </div>
            {car.color && (
              <div className="flex items-center bg-slate-50 px-2 py-1.5 rounded-xl gap-1.5">
                <div className="w-5 h-5 bg-rose-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <SafeIcon icon={LuPalette} className="h-3 w-3 text-rose-600" />
                </div>
                <span className="text-xs font-bold text-slate-700 truncate">{car.color}</span>
              </div>
            )}
            {car.transmission && (
              <div className="flex items-center bg-slate-50 px-2 py-1.5 rounded-xl gap-1.5">
                <div className={`w-5 h-5 rounded-lg flex items-center justify-center flex-shrink-0 ${car.transmission === 'automatic' ? 'bg-violet-100' : 'bg-amber-100'}`}>
                  <SafeIcon icon={LuSettings2} className={`h-3 w-3 ${car.transmission === 'automatic' ? 'text-violet-600' : 'text-amber-600'}`} />
                </div>
                <span className="text-xs font-bold text-slate-700 truncate">{car.transmission === 'automatic' ? 'أوتوماتيك' : 'يدوي'}</span>
              </div>
            )}
          </div>

          {hasDiscount && !isSold ? <div className="mb-2"><DiscountTimer car={car} compact /></div> : <div className="mb-2"><PricingBreakdown car={car} isSold={isSold} /></div>}

          <div className="flex-1" />

          <div className="border-t border-slate-100 pt-2 mt-1">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-1 text-slate-500 text-xs">
                <SafeIcon icon={LuEye} className="w-3 h-3" />
                <span className="font-medium">{(localViews || 0).toLocaleString()} مشاهدة</span>
              </div>
              <ShareMenu car={car} />
            </div>
            <button 
              onClick={handleWhatsApp}
              disabled={isSold}
              className={`w-full flex items-center justify-center font-bold py-2.5 px-3 rounded-xl transition-all text-sm gap-2 ${isSold ? 'bg-slate-100 text-slate-400 cursor-not-allowed' : hasDiscount ? 'bg-gradient-to-r from-red-500 to-rose-600 hover:from-red-600 hover:to-rose-700 text-white shadow-sm hover:shadow-md active:scale-[0.98]' : 'bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white shadow-sm hover:shadow-md active:scale-[0.98]'}`}
            >
              {isSold ? <><SafeIcon icon={LuCheckCircle} className="h-4 w-4" /> تم البيع</> : <>
                <svg viewBox="0 0 24 24" className="w-4 h-4 fill-current flex-shrink-0">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z" />
                  <path d="M12 0C5.373 0 0 5.373 0 12c0 2.123.554 4.118 1.528 5.845L.057 23.885l6.196-1.448A11.934 11.934 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 21.894a9.872 9.872 0 01-5.031-1.378l-.361-.214-3.741.981.999-3.648-.235-.374A9.867 9.867 0 012.106 12C2.106 6.58 6.58 2.106 12 2.106S21.894 6.58 21.894 12 17.42 21.894 12 21.894z" />
                </svg>
                تواصل عبر واتساب
              </>}
            </button>
          </div>
        </div>
      </motion.div>

      {lightboxOpen && pureImages.length > 0 && (
        <Lightbox 
          images={pureImages} 
          currentIndex={lightboxIndex} 
          onClose={() => setLightboxOpen(false)}
          onPrev={() => setLightboxIndex(i => i === 0 ? pureImages.length - 1 : i - 1)}
          onNext={(idx) => {
            if (typeof idx === 'number') setLightboxIndex(idx);
            else setLightboxIndex(i => i === pureImages.length - 1 ? 0 : i + 1);
          }}
        />
      )}
    </>
  );
};

export default CarCard;